import React, { useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { PartyPopper, RotateCcw, Send, ShieldCheck, Clock3, Sparkles } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { PROVINCE, TIPI_RICERCA, CARATTERISTICHE, ZONE, CAMERE, BAGNI, PAGAMENTO_ACQUISTO, TIPI_IMMOBILE, MQ_RANGES, DESTINAZIONI } from "@/data/formOptions";
import { Textarea } from "@/components/ui/textarea";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const initialForm = {
  tipo_ricerca: "",
  tipi_immobile: [],
  destinazione: "",
  provincia: "",
  comune: "",
  raggio_km: "",
  zone: [],
  camere_min: "",
  bagni_min: "",
  mq: "",
  budget_max: "",
  caratteristiche: [],
  periodo: "",
  num_persone: "",
  pagamento_acquisto: "",
  consulenza_broker: false,
  note: "",
  nome: "",
  whatsapp: "",
  email: "",
  privacy_accettata: false,
};

const fieldCls =
  "h-12 rounded-xl border-slate-200 bg-slate-50/60 focus:bg-white text-[#0A192F] text-[15px] transition-colors duration-200";

export const MatchForm = () => {
  const [form, setForm] = useState(initialForm);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(null);

  const set = (key, value) => setForm((f) => ({ ...f, [key]: value }));

  const toggleCaratteristica = (c) =>
    setForm((f) => ({
      ...f,
      caratteristiche: f.caratteristiche.includes(c)
        ? f.caratteristiche.filter((x) => x !== c)
        : [...f.caratteristiche, c],
    }));

  const toggleZona = (z) =>
    setForm((f) => ({
      ...f,
      zone: f.zone.includes(z) ? f.zone.filter((x) => x !== z) : [...f.zone, z],
    }));

  const toggleTipoImmobile = (t) =>
    setForm((f) => ({
      ...f,
      tipi_immobile: f.tipi_immobile.includes(t)
        ? f.tipi_immobile.filter((x) => x !== t)
        : [...f.tipi_immobile, t],
    }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.privacy_accettata) {
      toast.error("Devi accettare la Privacy Policy per continuare");
      return;
    }
    if (form.tipi_immobile.length === 0) {
      toast.error("Seleziona almeno un tipo di immobile");
      return;
    }
    if (!form.destinazione) {
      toast.error("Seleziona come userai l'immobile");
      return;
    }
    setSubmitting(true);
    try {
      const payload = { ...form, budget_max: Number(form.budget_max) };
      const { data } = await axios.post(`${API}/requests`, payload);
      setSubmitted({ ...payload, id: data.id });
      window.scrollTo({ top: document.getElementById("richiesta").offsetTop - 80, behavior: "smooth" });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Qualcosa è andato storto. Riprova tra poco.");
    } finally {
      setSubmitting(false);
    }
  };

  const reset = () => {
    setForm(initialForm);
    setSubmitted(null);
  };

  return (
    <section id="richiesta" className="relative py-20 sm:py-28">
      <div className="max-w-3xl mx-auto px-5 sm:px-8">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
          className="text-center mb-12"
        >
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-emerald-600 mb-4">
            La tua richiesta · 60 secondi
          </p>
          <h2 className="font-display text-3xl sm:text-4xl font-bold tracking-tight text-[#0A192F]">
            Descrivi la casa che cerchi. <span className="text-[#10B981]">Una volta sola.</span>
          </h2>
          <p className="mt-4 text-slate-500 text-sm sm:text-base max-w-xl mx-auto leading-relaxed">
            Comune, zone, camere, budget, note libere: più precisa è la tua richiesta, più mirati
            saranno i contatti che riceverai. Niente annunci a cui correre dietro: ti scrivono loro.
          </p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
          data-testid="match-form-container"
          className="bg-white rounded-[2rem] border border-slate-200/80 shadow-[0_20px_60px_rgba(10,25,47,0.08)] p-6 sm:p-10"
        >
          <AnimatePresence mode="wait">
            {submitted ? (
              <motion.div
                key="success"
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
                data-testid="success-confirmation-screen"
                className="text-center py-6"
              >
                <div className="mx-auto w-20 h-20 rounded-full bg-emerald-50 border-2 border-emerald-200 flex items-center justify-center mb-6">
                  <PartyPopper className="w-9 h-9 text-[#10B981]" />
                </div>
                <h3 className="font-display text-2xl sm:text-3xl font-extrabold text-[#0A192F] tracking-tight">
                  🎉 Richiesta presa in carico con successo!
                </h3>
                <p className="mt-4 text-slate-600 leading-relaxed max-w-md mx-auto">
                  Il nostro sistema sta già incrociando la tua richiesta con i portafogli di agenzie
                  e proprietari a <strong className="text-[#0A192F]">{submitted.comune} ({submitted.provincia})</strong>.
                  Appena scatta un Match, ti avvisiamo via email e parli direttamente con chi ha l'immobile
                  giusto nella <strong className="text-[#0A192F]">chat protetta</strong>: il tuo numero resta nascosto finché decidi tu.
                </p>
                <div className="mt-8 grid grid-cols-1 sm:grid-cols-3 gap-3 text-left">
                  {[
                    { icon: Clock3, t: "Tempo azzerato", d: "Smetti di cercare: il mercato bussa alla tua porta." },
                    { icon: ShieldCheck, t: "Zero spam", d: "Chi ti contatta paga per sbloccarti: solo offerte serie." },
                    { icon: Sparkles, t: "Chat Match", d: "Quando c'è un'offerta giusta, parli con chi la propone nella chat protetta." },
                  ].map(({ icon: Icon, t, d }) => (
                    <div key={t} className="rounded-2xl bg-slate-50 border border-slate-100 p-4">
                      <Icon className="w-5 h-5 text-[#10B981] mb-2" />
                      <p className="text-sm font-bold text-[#0A192F]">{t}</p>
                      <p className="text-xs text-slate-500 mt-1 leading-relaxed">{d}</p>
                    </div>
                  ))}
                </div>
                <button
                  onClick={reset}
                  data-testid="success-new-search-button"
                  className="mt-9 inline-flex items-center gap-2 rounded-full border-2 border-[#0A192F] text-[#0A192F] font-bold px-7 py-3 text-sm hover:bg-[#0A192F] hover:text-white transition-colors duration-300"
                >
                  <RotateCcw className="w-4 h-4" />
                  Inserisci un'altra ricerca
                </button>
              </motion.div>
            ) : (
              <motion.form
                key="form"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0, scale: 0.98 }}
                onSubmit={handleSubmit}
                className="space-y-7"
              >
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <Label htmlFor="tipo-ricerca" className="text-sm font-semibold text-[#0A192F]">Cosa cerchi? *</Label>
                    <Select value={form.tipo_ricerca} onValueChange={(v) => set("tipo_ricerca", v)} required>
                      <SelectTrigger id="tipo-ricerca" data-testid="match-form-tipo-ricerca" className={fieldCls}>
                        <SelectValue placeholder="Seleziona il tipo di ricerca" />
                      </SelectTrigger>
                      <SelectContent>
                        {TIPI_RICERCA.map((t) => (
                          <SelectItem key={t} value={t}>{t}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="provincia" className="text-sm font-semibold text-[#0A192F]">Provincia *</Label>
                    <Select value={form.provincia} onValueChange={(v) => set("provincia", v)} required>
                      <SelectTrigger id="provincia" data-testid="match-form-provincia" className={fieldCls}>
                        <SelectValue placeholder="Seleziona la provincia" />
                      </SelectTrigger>
                      <SelectContent className="max-h-72">
                        {PROVINCE.map((p) => (
                          <SelectItem key={p} value={p}>{p}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="space-y-3" data-testid="match-form-tipi-immobile">
                  <Label className="text-sm font-semibold text-[#0A192F]">Che tipo di immobile? * <span className="font-normal text-slate-400">(puoi sceglierne più di uno)</span></Label>
                  <div className="flex flex-wrap gap-2.5">
                    {TIPI_IMMOBILE.map((t) => (
                      <button
                        key={t}
                        type="button"
                        data-testid={`match-form-immobile-${t.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                        onClick={() => toggleTipoImmobile(t)}
                        className={`rounded-full px-4 py-2 text-sm font-medium transition-all duration-200 ${
                          form.tipi_immobile.includes(t)
                            ? "bg-[#0A192F] text-white shadow-[0_4px_14px_rgba(10,25,47,0.25)]"
                            : "bg-slate-50 border border-slate-200 text-slate-600 hover:border-slate-400"
                        }`}
                      >
                        {t}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="space-y-3" data-testid="match-form-destinazione">
                  <Label className="text-sm font-semibold text-[#0A192F]">Come la userai? *</Label>
                  <div className="flex flex-wrap gap-2.5">
                    {DESTINAZIONI.map((dest) => (
                      <button
                        key={dest}
                        type="button"
                        data-testid={`match-form-destinazione-${dest.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                        onClick={() => set("destinazione", dest)}
                        className={`rounded-full px-4 py-2 text-sm font-medium transition-all duration-200 ${
                          form.destinazione === dest
                            ? "bg-[#10B981] text-white shadow-[0_4px_14px_rgba(16,185,129,0.3)]"
                            : "bg-slate-50 border border-slate-200 text-slate-600 hover:border-slate-400"
                        }`}
                      >
                        {dest}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <Label htmlFor="comune" className="text-sm font-semibold text-[#0A192F]">Comune o Zona specifica *</Label>
                    <Input
                      id="comune"
                      data-testid="match-form-comune"
                      required
                      value={form.comune}
                      onChange={(e) => set("comune", e.target.value)}
                      placeholder="Es. Lecce centro, Gallipoli, Maglie..."
                      className={fieldCls}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="raggio" className="text-sm font-semibold text-[#0A192F]">Raggio di ricerca (km)</Label>
                    <Input
                      id="raggio"
                      data-testid="match-form-raggio"
                      type="number"
                      min="0"
                      value={form.raggio_km}
                      onChange={(e) => set("raggio_km", e.target.value)}
                      placeholder="Es. 10 (vuoto = solo il comune)"
                      className={fieldCls}
                    />
                  </div>
                </div>

                {(form.tipo_ricerca || "").includes("Affitto") && (
                  <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: "auto" }}
                    className="space-y-2"
                  >
                    <Label htmlFor="periodo" className="text-sm font-semibold text-[#0A192F]">
                      Periodo richiesto <span className="font-normal text-slate-400">(per gli affitti)</span>
                    </Label>
                    <Input
                      id="periodo"
                      data-testid="match-form-periodo"
                      value={form.periodo}
                      onChange={(e) => set("periodo", e.target.value)}
                      placeholder="Es. 10–17 agosto 2027, oppure da maggio a settembre 2027"
                      className={fieldCls}
                    />
                  </motion.div>
                )}

                <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
                  <div className="space-y-2">
                    <Label htmlFor="camere" className="text-sm font-semibold text-[#0A192F]">Camere da letto minime</Label>
                    <Select value={form.camere_min} onValueChange={(v) => set("camere_min", v)}>
                      <SelectTrigger id="camere" data-testid="match-form-camere" className={fieldCls}>
                        <SelectValue placeholder="Indifferente" />
                      </SelectTrigger>
                      <SelectContent>
                        {CAMERE.map((c) => (
                          <SelectItem key={c} value={c}>{c === "5+" ? "5 o più" : c}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="bagni" className="text-sm font-semibold text-[#0A192F]">Bagni minimi</Label>
                    <Select value={form.bagni_min} onValueChange={(v) => set("bagni_min", v)}>
                      <SelectTrigger id="bagni" data-testid="match-form-bagni" className={fieldCls}>
                        <SelectValue placeholder="Indifferente" />
                      </SelectTrigger>
                      <SelectContent>
                        {BAGNI.map((b) => (
                          <SelectItem key={b} value={b}>{b === "3+" ? "3 o più" : b}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="mq" className="text-sm font-semibold text-[#0A192F]">Metri quadri circa</Label>
                    <Select value={form.mq} onValueChange={(v) => set("mq", v)}>
                      <SelectTrigger id="mq" data-testid="match-form-mq" className={fieldCls}>
                        <SelectValue placeholder="Indifferente" />
                      </SelectTrigger>
                      <SelectContent>
                        {MQ_RANGES.map((m) => (
                          <SelectItem key={m} value={m}>{m}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="space-y-3" data-testid="match-form-zone">
                  <Label className="text-sm font-semibold text-[#0A192F]">Dove preferisci? <span className="font-normal text-slate-400">(puoi sceglierne più di una)</span></Label>
                  <div className="flex flex-wrap gap-2.5">
                    {ZONE.map((z) => (
                      <button
                        key={z}
                        type="button"
                        data-testid={`match-form-zona-${z.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                        onClick={() => toggleZona(z)}
                        className={`rounded-full px-4 py-2 text-sm font-medium transition-all duration-200 ${
                          form.zone.includes(z)
                            ? "bg-[#10B981] text-white shadow-[0_4px_14px_rgba(16,185,129,0.3)]"
                            : "bg-slate-50 border border-slate-200 text-slate-600 hover:border-slate-400"
                        }`}
                      >
                        {z}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <Label htmlFor="budget" className="text-sm font-semibold text-[#0A192F]">Budget Massimo Disponibile (€) *</Label>
                    <Input
                      id="budget"
                      data-testid="match-form-budget"
                      required
                      type="number"
                      min="1"
                      value={form.budget_max}
                      onChange={(e) => set("budget_max", e.target.value)}
                      placeholder="Es. 150000 oppure 600"
                      className={fieldCls}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="note" className="text-sm font-semibold text-[#0A192F]">Altre richieste o dettagli <span className="font-normal text-slate-400">(facoltativo)</span></Label>
                    <Textarea
                      id="note"
                      data-testid="match-form-note"
                      value={form.note}
                      onChange={(e) => set("note", e.target.value)}
                      placeholder="Es. piano alto, vicino scuole, no spese condominiali, periodo vacanze agosto..."
                      className="rounded-xl border-slate-200 bg-slate-50/60 focus:bg-white text-[#0A192F] text-[15px] min-h-[52px] transition-colors duration-200"
                    />
                  </div>
                </div>

                {form.tipo_ricerca === "Acquisto Casa" && (
                  <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: "auto" }}
                    className="rounded-2xl border border-emerald-200 bg-emerald-50/50 p-5 space-y-4"
                    data-testid="match-form-pagamento-section"
                  >
                    <div className="space-y-2">
                      <Label htmlFor="pagamento" className="text-sm font-semibold text-[#0A192F]">
                        Come pagheresti? <span className="font-normal text-slate-400">(ci aiuta a proporti solo soluzioni realistiche)</span>
                      </Label>
                      <Select value={form.pagamento_acquisto} onValueChange={(v) => set("pagamento_acquisto", v)}>
                        <SelectTrigger id="pagamento" data-testid="match-form-pagamento" className={fieldCls}>
                          <SelectValue placeholder="Seleziona un'opzione" />
                        </SelectTrigger>
                        <SelectContent>
                          {PAGAMENTO_ACQUISTO.map((p) => (
                            <SelectItem key={p} value={p}>{p}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    {(form.pagamento_acquisto === "Devo fare un mutuo" || form.pagamento_acquisto === "Mutuo: studio di fattibilità da fare") && (
                      <motion.label
                        initial={{ opacity: 0, y: 8 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="flex items-start gap-3 cursor-pointer rounded-xl border border-emerald-300 bg-white px-4 py-4"
                      >
                        <Checkbox
                          data-testid="match-form-broker"
                          checked={form.consulenza_broker}
                          onCheckedChange={(v) => set("consulenza_broker", v === true)}
                          className="mt-0.5 data-[state=checked]:bg-[#10B981] data-[state=checked]:border-[#10B981]"
                        />
                        <span className="text-sm text-slate-700 leading-relaxed">
                          <strong className="text-[#0A192F]">Sì, voglio la consulenza mutuo gratuita.</strong>{" "}
                          Nella tua zona abbiamo un broker di fiducia: studio di fattibilità e consulenza
                          senza impegno e senza costi per te.
                        </span>
                      </motion.label>
                    )}
                  </motion.div>
                )}

                <div className="space-y-3" data-testid="match-form-caratteristiche">
                  <Label className="text-sm font-semibold text-[#0A192F]">Caratteristiche indispensabili</Label>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {CARATTERISTICHE.map((c) => (
                      <label
                        key={c}
                        className={`flex items-center gap-3 rounded-xl border px-4 py-3.5 cursor-pointer transition-all duration-200 ${
                          form.caratteristiche.includes(c)
                            ? "border-[#10B981] bg-emerald-50/60 shadow-[0_4px_14px_rgba(16,185,129,0.12)]"
                            : "border-slate-200 bg-slate-50/60 hover:border-slate-300"
                        }`}
                      >
                        <Checkbox
                          data-testid={`match-form-caratteristica-${c.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                          checked={form.caratteristiche.includes(c)}
                          onCheckedChange={() => toggleCaratteristica(c)}
                          className="data-[state=checked]:bg-[#10B981] data-[state=checked]:border-[#10B981]"
                        />
                        <span className="text-sm font-medium text-[#0A192F]">{c}</span>
                      </label>
                    ))}
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <Label htmlFor="nome" className="text-sm font-semibold text-[#0A192F]">Il tuo Nome *</Label>
                    <Input
                      id="nome"
                      data-testid="match-form-nome"
                      required
                      value={form.nome}
                      onChange={(e) => set("nome", e.target.value)}
                      placeholder="Es. Mario Rossi"
                      className={fieldCls}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="whatsapp" className="text-sm font-semibold text-[#0A192F]">Il tuo numero WhatsApp (per ricevere i match) *</Label>
                    <Input
                      id="whatsapp"
                      data-testid="match-form-whatsapp"
                      required
                      type="tel"
                      value={form.whatsapp}
                      onChange={(e) => set("whatsapp", e.target.value)}
                      placeholder="Es. 333 1234567"
                      className={fieldCls}
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="email" className="text-sm font-semibold text-[#0A192F]">La tua Email <span className="font-normal text-slate-400">(facoltativa, per ricevere i match anche via email)</span></Label>
                  <Input
                    id="email"
                    data-testid="match-form-email"
                    type="email"
                    value={form.email}
                    onChange={(e) => set("email", e.target.value)}
                    placeholder="Es. mario.rossi@email.it"
                    className={fieldCls}
                  />
                </div>

                <label className="flex items-start gap-3 cursor-pointer rounded-xl border border-slate-200 bg-slate-50/60 px-4 py-4">
                  <Checkbox
                    data-testid="match-form-privacy"
                    required
                    checked={form.privacy_accettata}
                    onCheckedChange={(v) => set("privacy_accettata", v === true)}
                    className="mt-0.5 data-[state=checked]:bg-[#10B981] data-[state=checked]:border-[#10B981]"
                  />
                  <span className="text-sm text-slate-600 leading-relaxed">
                    Accetto la{" "}
                    <Link
                      to="/privacy-policy"
                      data-testid="match-form-privacy-link"
                      target="_blank"
                      className="font-semibold text-[#0A192F] underline underline-offset-2 hover:text-[#10B981] transition-colors"
                    >
                      Privacy Policy
                    </Link>{" "}
                    e autorizzo la cessione del mio contatto, a pagamento, ad agenzie o privati interessati a
                    propormi un immobile. *
                  </span>
                </label>

                <button
                  type="submit"
                  disabled={submitting}
                  data-testid="match-form-submit-button"
                  className="group w-full inline-flex items-center justify-center gap-3 rounded-full bg-[#10B981] text-white font-extrabold text-base sm:text-lg tracking-wide px-8 py-5 shadow-[0_10px_30px_rgba(16,185,129,0.4)] hover:bg-[#059669] hover:shadow-[0_14px_36px_rgba(16,185,129,0.5)] hover:-translate-y-0.5 active:translate-y-0 transition-all duration-300 disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  {submitting ? (
                    <>
                      <span className="w-5 h-5 border-2 border-white/40 border-t-white rounded-full animate-spin" />
                      Invio in corso...
                    </>
                  ) : (
                    <>
                      <Send className="w-5 h-5 group-hover:translate-x-1 group-hover:-translate-y-0.5 transition-transform duration-300" />
                      ATTIVA IL MATCHING GRATIS
                    </>
                  )}
                </button>
              </motion.form>
            )}
          </AnimatePresence>
        </motion.div>
      </div>
    </section>
  );
};
